import { FreeCamera, Scene, Vector3 } from '@babylonjs/core';
import type { CameraController } from './CameraController.js';

/**
 * GTA-style teleport cinematic. Two distinct visual modes depending on the active camera:
 *
 *   - **Chase active**: spawn an internal perspective camera, position it to exactly match
 *     the chase camera's current pose (no visual cut), then smoothly lerp it up and into
 *     a top-down peak pose. Reverse on inbound — the cinematic camera ends at the chase
 *     camera's destination pose so the hand-off is invisible.
 *
 *   - **Top-down active**: don't switch cameras at all. The active top-down ortho stays
 *     bound to the car; we just animate its ortho extents from 1× to ~6× (zoom out) and
 *     back (zoom in). This is what the user explicitly asked for ("if you already have
 *     the top-down camera, you just zoom it out").
 *
 * The white cloud overlay (fullscreen div) fades in/out alongside the camera motion so
 * the actual teleport happens behind full coverage and is never visible.
 *
 * `setCovered` instantly puts the cinematic camera at peak top-down with the overlay
 * opaque — used at game start so the page opens already covered, before chunks load.
 */

const FULL_PEAK_HEIGHT = 560;
const SHORT_PEAK_HEIGHT = 180;
const FULL_DURATION_SEC = 1.4;
const SHORT_DURATION_SEC = 0.7;
const FULL_ZOOM_FACTOR = 11;
const SHORT_ZOOM_FACTOR = 4;
const DEFAULT_FOV = 0.9;
/** "See-through" peak opacity used during the zoom-out / zoom-in MOTION phases. Kept below
 *  1.0 (and with `--cover-veil` at 0) so the scene stays faintly visible through the thin
 *  parts of the cloud texture — you can see a bit of the world as you rise and descend. */
const COVER_PEAK_OPACITY = 0.96;
/** Fully-concealing state used during the world-swap + hold: element opacity 1.0 AND the
 *  solid `--cover-veil` layer at 1.0, so the cloud gaps are filled and the screen is fully
 *  opaque. This is what hides the old→new world change (see `deepen`). */
const COVER_FULL = 1;

interface Pose {
  position: Vector3;
  target: Vector3;
  fov: number;
}

type Variant = 'full' | 'short';

interface PlayOpts {
  cameraCtl: CameraController;
  /** Live getter for the current vehicle position (NOT a snapshot). The cinematic calls
   *  this each frame so the camera tracks the vehicle as it settles after a teleport
   *  drop — otherwise the camera ends pointed at the spawn-height Y while the vehicle
   *  has already dropped to its rest height, producing a visible jump at hand-off. */
  getCarPos: () => Vector3;
  getCarForward: () => Vector3;
  variant: Variant;
}

export class TeleportCinematic {
  private scene: Scene;
  private camera: FreeCamera;
  private overlay: HTMLDivElement;
  /** Optional hook fired at the start of each zoom-out / zoom-in so the host can play a
   *  whoosh SFX in sync with the camera rush. Receives the move's wall-clock duration so the
   *  sound can span exactly the camera motion. Kept as a callback so the cinematic stays
   *  decoupled from the audio system. */
  private onWhoosh?: (dir: 'out' | 'in', seconds: number) => void;

  constructor(scene: Scene) {
    this.scene = scene;
    this.camera = new FreeCamera('teleport-cinematic', new Vector3(0, 100, 0), scene);
    this.camera.fov = DEFAULT_FOV;
    this.camera.minZ = 0.1;
    this.camera.maxZ = 5000;

    // Visuals (layered cloud puffs + drift animation) live in styles.css under
    // `#teleport-clouds`; here we only set the non-visual layout + the JS-controlled
    // opacity (the cinematic animates opacity directly each frame).
    this.overlay = document.createElement('div');
    this.overlay.id = 'teleport-clouds';
    this.overlay.style.cssText = [
      'position: fixed',
      'inset: 0',
      'pointer-events: none',
      'opacity: 0',
      'z-index: 100',
    ].join(';');
    document.body.appendChild(this.overlay);
  }

  getCamera(): FreeCamera {
    return this.camera;
  }

  /** Register the whoosh SFX trigger. Called once at boot from main.ts. */
  setWhoosh(cb: (dir: 'out' | 'in', seconds: number) => void): void {
    this.onWhoosh = cb;
  }

  /** Cover the screen with clouds AND park the right camera so the next inbound starts
   *  cleanly. Branches by active mode:
   *
   *    - chase mode → puts the cinematic perspective camera at peak top-down above
   *      `centerXZ`, and sets it as the active camera. The inbound chase morph lerps
   *      this camera down to the chase pose.
   *    - topdown mode → keeps the topdown ortho camera active and just sets its ortho
   *      extents to the peak zoom factor. The inbound topdown zoom animates the extents
   *      back to 1× — all visible on the topdown camera, no perspective swap.
   *
   *  This split is what prevents the "instant snap" hand-off at the end of a topdown
   *  inbound: if we put the cinematic perspective camera in front during the cover, the
   *  topdown ortho zoom animation runs invisibly and only becomes visible on the final
   *  hand-off frame.
   */
  /** Drive the two cover knobs together: `opacity` of the whole overlay (0 = revealed) and
   *  `veil` (0..1) of the solid backdrop that fills the cloud gaps (1 = fully opaque screen).
   *  Motion phases use veil 0 (see-through); the swap/hold use veil 1 (fully concealed). */
  private setCover(opacity: number, veil: number): void {
    this.overlay.style.opacity = String(opacity);
    this.overlay.style.setProperty('--cover-veil', String(veil));
    // Run the (repaint-heavy) cloud drift animation only while the cover is visible — see the
    // `.active` rules in styles.css. Avoids burning raster time on an invisible overlay in-game.
    this.overlay.classList.toggle('active', opacity > 0);
  }

  /** Smoothly thicken the cover from its current see-through state to fully opaque, in place.
   *  Called once the new world's data is ready but BEFORE the world-swap, so the swap (old
   *  terrain → new terrain) happens behind a solid screen and is never visible. */
  async deepen(seconds: number): Promise<void> {
    const startOp = parseFloat(this.overlay.style.opacity) || COVER_PEAK_OPACITY;
    await this.animate((t) => {
      const e = easeInOut(t);
      this.setCover(lerp(startOp, COVER_FULL, e), e);
    }, seconds);
    this.setCover(COVER_FULL, 1);
  }

  setCovered(opts: { cameraCtl: CameraController; centerXZ?: { x: number; z: number }; variant: Variant }): void {
    this.setCover(COVER_FULL, 1);
    if (opts.cameraCtl.getMode() === 'topdown') {
      opts.cameraCtl.setTopDownZoom(zoomFactor(opts.variant));
      this.scene.activeCamera = opts.cameraCtl.activeCamera();
    } else {
      const peak = peakHeight(opts.variant);
      const xz = opts.centerXZ ?? { x: 0, z: 0 };
      applyPose(this.camera, {
        position: new Vector3(xz.x, peak, xz.z),
        target: new Vector3(xz.x, 0, xz.z + 0.5),
        fov: DEFAULT_FOV,
      });
      this.scene.activeCamera = this.camera;
    }
  }

  async playOutbound(opts: PlayOpts): Promise<void> {
    this.onWhoosh?.('out', durationSec(opts.variant));
    if (opts.cameraCtl.getMode() === 'topdown') {
      await this.outboundTopdownZoom(opts);
    } else {
      await this.outboundChaseMorph(opts);
    }
  }

  /** Hold at peak (clouds opaque, no animation) for `seconds`. Used between teleport and
   *  inbound so physics has dedicated wall-clock time to settle the chassis before the
   *  camera descends and reveals it. */
  hold(seconds: number): Promise<void> {
    this.setCover(COVER_FULL, 1);
    return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
  }

  async playInbound(opts: PlayOpts): Promise<void> {
    this.onWhoosh?.('in', durationSec(opts.variant));
    if (opts.cameraCtl.getMode() === 'topdown') {
      await this.inboundTopdownZoom(opts);
    } else {
      await this.inboundChaseMorph(opts);
    }
  }

  // ---- Top-down active: zoom ortho extents only, no camera switch. ----

  private async outboundTopdownZoom(opts: PlayOpts): Promise<void> {
    // Ensure the topdown camera is the active one — otherwise the zoom animation runs on
    // a camera that isn't being rendered, and the user only sees the change on the final
    // hand-off frame.
    this.scene.activeCamera = opts.cameraCtl.activeCamera();
    const factor = zoomFactor(opts.variant);
    const duration = durationSec(opts.variant);
    const startZoom = opts.cameraCtl.getTopDownZoom();
    await this.animate((t) => {
      const eased = easeInOut(t);
      opts.cameraCtl.setTopDownZoom(lerp(startZoom, factor, eased));
      // See-through while rising (veil 0) so the world below stays faintly visible.
      this.setCover(clamp01(t * 1.4) * COVER_PEAK_OPACITY, 0);
    }, duration);
    opts.cameraCtl.setTopDownZoom(factor);
    this.setCover(COVER_PEAK_OPACITY, 0);
  }

  private async inboundTopdownZoom(opts: PlayOpts): Promise<void> {
    this.scene.activeCamera = opts.cameraCtl.activeCamera();
    const factor = zoomFactor(opts.variant);
    const duration = durationSec(opts.variant);
    const startZoom = opts.cameraCtl.getTopDownZoom() || factor;
    // Start fully concealed (the swap happened behind this) and crossfade to the revealed
    // new world as we descend — veil drops with opacity so the solid backdrop melts away.
    this.setCover(COVER_FULL, 1);
    await this.animate((t) => {
      const eased = easeInOut(t);
      opts.cameraCtl.setTopDownZoom(lerp(startZoom, 1, eased));
      const o = clamp01(1 - t * 1.4);
      this.setCover(o, o);
    }, duration);
    opts.cameraCtl.setTopDownZoom(1);
    this.setCover(0, 0);
  }

  // ---- Chase active: morph cinematic camera between chase pose and top-down peak. ----

  private async outboundChaseMorph(opts: PlayOpts): Promise<void> {
    const peak = peakHeight(opts.variant);
    const duration = durationSec(opts.variant);

    // Snapshot the chase camera's CURRENT position so the camera swap is invisible —
    // the cinematic camera must take over at the exact pixel the chase camera was at.
    // (Target/fov are recomputed live so vehicle tracking still works.)
    const chaseCam = opts.cameraCtl.activeCamera() as FreeCamera;
    const fixedStartPosition = chaseCam.position.clone();
    const fixedStartFov = chaseCam.fov;

    applyPose(this.camera, {
      position: fixedStartPosition,
      target: chasePoseTarget(opts.getCarPos(), opts.getCarForward()),
      fov: fixedStartFov,
    });
    this.scene.activeCamera = this.camera;

    await this.animate((t) => {
      const eased = easeInOut(t);
      const carPos = opts.getCarPos();
      const carFwd = opts.getCarForward();
      const startPose: Pose = {
        position: fixedStartPosition,
        target: chasePoseTarget(carPos, carFwd),
        fov: fixedStartFov,
      };
      const endPose = topdownPeakPose(carPos, peak);
      applyPose(this.camera, lerpPose(startPose, endPose, eased));
      // See-through while rising (veil 0) so the world below stays faintly visible.
      this.setCover(clamp01(t * 1.4) * COVER_PEAK_OPACITY, 0);
    }, duration);
    this.setCover(COVER_PEAK_OPACITY, 0);
  }

  private async inboundChaseMorph(opts: PlayOpts): Promise<void> {
    const peak = peakHeight(opts.variant);
    const duration = durationSec(opts.variant);

    const initialCarPos = opts.getCarPos();
    applyPose(this.camera, topdownPeakPose(initialCarPos, peak));
    this.scene.activeCamera = this.camera;
    // Start fully concealed (the swap happened behind this) and crossfade to the revealed
    // new world as we descend — veil drops with opacity so the solid backdrop melts away.
    this.setCover(COVER_FULL, 1);

    await this.animate((t) => {
      const eased = easeInOut(t);
      // Both endpoints follow the LIVE vehicle position each frame. As the chassis
      // settles on its suspension during the descent, the end pose drops with it — so
      // the cinematic camera ends exactly where the chase camera will take over, no jump.
      const carPos = opts.getCarPos();
      const carFwd = opts.getCarForward();
      const startPose = topdownPeakPose(carPos, peak);
      const endPose = chasePose(carPos, carFwd);
      applyPose(this.camera, lerpPose(startPose, endPose, eased));
      const o = clamp01(1 - t * 1.4);
      this.setCover(o, o);
    }, duration);

    // Snap the real chase camera to the now-settled vehicle so the hand-off matches.
    opts.cameraCtl.snap(opts.getCarPos(), opts.getCarForward());
    this.setCover(0, 0);
  }

  private animate(step: (t: number) => void, durationSec: number): Promise<void> {
    return new Promise((resolve) => {
      const start = performance.now();
      const observer = this.scene.onBeforeRenderObservable.add(() => {
        const elapsed = (performance.now() - start) / 1000;
        const t = elapsed / durationSec;
        if (t >= 1) {
          step(1);
          this.scene.onBeforeRenderObservable.remove(observer);
          resolve();
          return;
        }
        step(t);
      });
    });
  }
}

// --- helpers ---

function applyPose(camera: FreeCamera, pose: Pose): void {
  camera.position.copyFrom(pose.position);
  camera.setTarget(pose.target);
  camera.fov = pose.fov;
}

function lerpPose(a: Pose, b: Pose, t: number): Pose {
  return {
    position: Vector3.Lerp(a.position, b.position, t),
    target: Vector3.Lerp(a.target, b.target, t),
    fov: a.fov + (b.fov - a.fov) * t,
  };
}

// These constants mirror ChaseCamera so the cinematic's "chase pose" matches what the
// real chase camera will be at when we hand off — invisible cut.
const CHASE_FOLLOW_DISTANCE = 7.5;
const CHASE_FOLLOW_HEIGHT = 3.0;
const CHASE_TARGET_FORWARD = 2;
const CHASE_TARGET_UP = 1.0;

/** The point a chase-mode camera looks at — mirrors ChaseCamera's internal formula. */
function chasePoseTarget(carPos: Vector3, forward: Vector3): Vector3 {
  return carPos.add(new Vector3(0, CHASE_TARGET_UP, 0)).add(forward.scale(CHASE_TARGET_FORWARD));
}

/** Full chase pose (position + target + fov) — mirrors ChaseCamera so hand-off matches. */
function chasePose(carPos: Vector3, forward: Vector3): Pose {
  return {
    position: carPos
      .add(forward.scale(-CHASE_FOLLOW_DISTANCE))
      .add(new Vector3(0, CHASE_FOLLOW_HEIGHT, 0)),
    target: chasePoseTarget(carPos, forward),
    fov: DEFAULT_FOV,
  };
}

function topdownPeakPose(carPos: Vector3, peak: number): Pose {
  return {
    position: new Vector3(carPos.x, carPos.y + peak, carPos.z),
    // Slight +Z target offset = north-up screen orientation without gimbal lock.
    target: new Vector3(carPos.x, carPos.y, carPos.z + 0.5),
    fov: DEFAULT_FOV,
  };
}

function peakHeight(v: Variant): number {
  return v === 'short' ? SHORT_PEAK_HEIGHT : FULL_PEAK_HEIGHT;
}

function durationSec(v: Variant): number {
  return v === 'short' ? SHORT_DURATION_SEC : FULL_DURATION_SEC;
}

function zoomFactor(v: Variant): number {
  return v === 'short' ? SHORT_ZOOM_FACTOR : FULL_ZOOM_FACTOR;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - 2 * (1 - t) * (1 - t);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
